import React, { useState } from 'react';
import { Lock, LogOut, Download, Trash2, User, Palette, Share2 } from 'lucide-react';
import Header from '../components/Header';
import Notifications from '../components/Notifications';
import ThemeSelector from '../components/ThemeSelector';
import ShareStreak from '../components/ShareStreak';
import SupportResources from '../components/SupportResources';
import SyncSettings from '../components/SyncSettings';
import InstallHelp from '../components/InstallHelp';
import { PinSetup, usePinLock } from '../components/PinLock';
import { useApp } from '../context/AppContext';

const Toggle = ({ value, onChange }) => (
  <button
    onClick={() => onChange(!value)}
    className={`w-12 h-6 rounded-full transition-all flex items-center px-1 ${value ? 'bg-brand-500' : 'bg-stone-300'}`}
  >
    <div className={`w-4 h-4 bg-white rounded-full shadow transition-all ${value ? 'translate-x-6' : ''}`} />
  </button>
);

const SettingsScreen = () => {
  const { userData, setUserData, resetAll } = useApp();
  const { isEnabled: pinEnabled, disable: disablePin } = usePinLock();
  const [name, setName] = useState(userData.name);
  const [saved, setSaved] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [pinOn, setPinOn] = useState(pinEnabled);

  const handleSave = () => {
    setUserData(prev => ({ ...prev, name }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleExport = () => {
    const data = { userData, exportDate: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recovery-data.json';
    a.click();
  };

  const handlePinToggle = (val) => {
    if (val) {
      setShowPinSetup(true);
    } else {
      disablePin();
      setPinOn(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-stone-50 pb-24">
      <Header title="הגדרות" subtitle="נהל את האפליקציה" />

      {showShare && <ShareStreak onClose={() => setShowShare(false)} />}
      {showPinSetup && (
        <PinSetup
          onSave={() => { setShowPinSetup(false); setPinOn(true); }}
          onCancel={() => setShowPinSetup(false)}
        />
      )}

      <div className="px-4 py-4 space-y-4">
        {/* Profile */}
        <div className="bg-white rounded-xl p-4 shadow space-y-3">
          <h3 className="font-bold text-stone-800 flex items-center gap-2">
            <User className="w-4 h-4 text-brand-500" /> פרופיל
          </h3>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="השם שלך"
            className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          <button
            onClick={handleSave}
            className={`w-full py-2 rounded-lg font-semibold text-sm transition ${
              saved ? 'bg-brand-500 text-white' : 'bg-brand-500 text-white hover:bg-brand-600'
            }`}
          >
            {saved ? '✅ נשמר!' : 'שמור שם'}
          </button>
        </div>

        {/* Human Support */}
        <SupportResources />

        {/* Theme */}
        <ThemeSelector />

        {/* Share Streak */}
        <div className="bg-white rounded-xl p-4 shadow">
          <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
            <Share2 className="w-4 h-4 text-brand-500" /> שתף הרצף
          </h3>
          <button
            onClick={() => setShowShare(true)}
            className="w-full bg-gradient-to-r from-brand-500 to-gold-500 text-white py-3 rounded-xl font-bold hover:opacity-90 transition"
          >
            📸 צור תמונת רצף לשיתוף
          </button>
        </div>

        {/* Notifications */}
        <Notifications />

        {/* Security */}
        <div className="bg-white rounded-xl p-4 shadow">
          <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
            <Lock className="w-4 h-4 text-brand-500" /> אבטחה
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-stone-800 text-sm">נעילת PIN</p>
              <p className="text-xs text-stone-500">הגן על הפרטיות שלך</p>
            </div>
            <Toggle value={pinOn} onChange={handlePinToggle} />
          </div>
        </div>

        {/* Sync */}
        <SyncSettings />

        {/* Install */}
        <InstallHelp />

        {/* Data */}
        <div className="bg-white rounded-xl p-4 shadow">
          <h3 className="font-bold text-stone-800 mb-3">📦 נתונים</h3>
          <button onClick={handleExport} className="w-full flex items-center gap-3 py-2 text-sm font-semibold text-stone-700 hover:text-brand-500 border-b border-stone-100 transition">
            <Download className="w-4 h-4" /> ייצא נתונים (JSON)
          </button>
          <button onClick={() => setShowReset(true)} className="w-full flex items-center gap-3 py-2 text-sm font-semibold text-red-500 hover:text-red-600 transition">
            <Trash2 className="w-4 h-4" /> אפס את כל הנתונים
          </button>
        </div>

        {showReset && (
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <p className="font-bold text-red-700 mb-3">⚠️ האם אתה בטוח?</p>
            <div className="flex gap-2">
              <button onClick={() => { resetAll(); setShowReset(false); }} className="flex-1 bg-red-500 text-white py-2 rounded-lg font-bold">מחק הכל</button>
              <button onClick={() => setShowReset(false)} className="flex-1 bg-stone-200 text-stone-700 py-2 rounded-lg font-bold">ביטול</button>
            </div>
          </div>
        )}

        <div className="text-center py-4">
          <p className="text-2xl mb-2">💙</p>
          <p className="font-bold text-stone-800">יומן גמילה v2.0</p>
          <p className="text-xs text-stone-400 mt-1">PWA | localStorage | עובד אופליין</p>
        </div>
      </div>
    </div>
  );
};

export default SettingsScreen;
